import React, { useEffect, useMemo } from "react";
import ImagesCarousel2 from "components/ImagesCarousel2";
import { formatPrice } from "utils/formatPrice";
import { useNavigate } from "react-router-dom";
import { Button, Img, Heading, Text } from "./..";

const DEFAULT_IMG = { image_url: "/images/img_car11_qgcqjcnfp_428x738.png", __isDefault: true };

const statusMap = {
  0: { txt: "ממתין להצעה", clr: "#808080cc" },
  1: { txt: "ממתין לאישור", clr: "#CE0000" },
  2: { txt: "מאושר", clr: "#619D1C" },
  3: { txt: "הצעה שנדחתה", clr: "#666666" },
  4: { txt: "אושר לאחר", clr: "#619D1C" },
};

const auctionStatusMap = {
  active: { txt: "מכרז פעיל", clr: "#f59e0b" },
  future: { txt: "מכרז עתידי", clr: "#6b7280" },
  ended: { txt: "מכרז הסתיים", clr: "#6b7280" },
  inactive: { txt: "מכרז לא פעיל", clr: "#808080cc" },
};

function truthy(value) {
  return value === true || value === 1 || value === "1" || value === "true";
}

function detectAuctionPhase(row = {}) {
  if (!truthy(row.is_auction ?? row.isAuction)) return null;
  if (truthy(row.auction_is_active)) return "active";
  if (truthy(row.auction_has_ended)) return "ended";

  const status = String(row.auction_status || row.status || "").toLowerCase();
  if (["ended", "finalized"].includes(status)) return "ended";
  if (["active", "live"].includes(status)) return "active";

  const start = row.auction_start_datetime || row.auction_start_at;
  if (start) {
    const startTime = new Date(String(start).replace(" ", "T")).getTime();
    if (!Number.isNaN(startTime) && startTime > Date.now()) return "future";
  }

  return "inactive";
}

export default function CarSummaryPreview({
  id,
  year,
  manufacturer,
  model,
  hand,
  gearbox,
  engine_type,
  images = [],
  offer_status,
  offered_price,
  approved_other,
  ...props
}) {
  // ✅ ממירים כל תמונה ל־{image_url: "..."} כדי ש־ImagesCarousel2 לא יקבל File בלי image_url
  const imagesSafe = useMemo(() => {
    const arr = Array.isArray(images) ? images : [];
    if (arr.length === 0) return [DEFAULT_IMG];

    return arr
      .map((img) => {
        if (!img) return null;

        // אם זה כבר האובייקט שהקרוסלה מכירה
        if (typeof img?.image_url === "string" && img.image_url.trim()) return img;

        // אם זה סטרינג (למשל "/images/..." או "https://...")
        if (typeof img === "string" && img.trim()) return { image_url: img };

        // אם זה File/Blob (שלב העלאה)
        if (img instanceof Blob) {
          return { image_url: URL.createObjectURL(img), __isBlob: true };
        }

        // אם יש לך פורמט אחר לפעמים
        if (typeof img?.url === "string" && img.url.trim()) return { image_url: img.url };

        return null;
      })
      .filter(Boolean);
  }, [images]);

  // ✅ ניקוי blob urls כדי שלא יהיה memory leak
  useEffect(() => {
    return () => {
      imagesSafe.forEach((x) => {
        if (x?.__isBlob && typeof x?.image_url === "string") {
          try { URL.revokeObjectURL(x.image_url); } catch {}
        }
      });
    };
  }, [imagesSafe]);

  const formatOfferText = (text) => {
    if (text === null || text === undefined || text === "") return "";
    const s = String(text).trim();
    if (s.includes("-")) {
      return s
        .split("-")
        .map((v) => formatPrice(String(v).trim()))
        .join(" - ");
    }
    return formatPrice(s);
  };

  const displayStatus = Number(offer_status) === 0 && Number(approved_other) === 1 ? 4 : Number(offer_status);

  const offerText = props.offer_range ?? offered_price ?? "";
  const hasOfferText = offerText !== null && offerText !== undefined && String(offerText).trim() !== "";

  // If this car is an auction, prefer auction label/colour over the simple offer_status map
  const auctionPhase = detectAuctionPhase({
    is_auction: props.is_auction ?? props.isAuction,
    auction_is_active: props.auction_is_active,
    auction_has_ended: props.auction_has_ended,
    auction_status: props.auction_status,
    auction_start_datetime: props.auction_start_datetime,
    auction_start_at: props.auction_start_at,
  });

  let txt, clr;
  if (auctionPhase) {
    const a = auctionStatusMap[auctionPhase] || auctionStatusMap.inactive;
    txt = a.txt;
    clr = a.clr;
  } else {
    ({ txt, clr } = statusMap[displayStatus] ?? statusMap[0]);
  }

  const navigate = useNavigate();
  const navigateCarProfil = () => navigate(`${id}`);

  return (
    <div {...props} className={`${props.className} relative flex flex-col w-full rounded-[16px]`}>
      <div className="relative">
        <Button
          shape="round"
          className="ml-[15px] mt-[17px] rounded-[14px] font-medium capitalize text-white font-[DM Sans] sm:px-5"
          style={{
            zIndex: "999",
            backgroundColor: clr,
            color: "white",
            paddingTop: "14px",
            paddingBottom: "14px",
            paddingRight: "20px",
            paddingLeft: "20px",
            position: "absolute",
          }}
        >
          {txt}
        </Button>

        {hasOfferText && (
          <div className="absolute w-full bottom-0 flex justify-center" style={{ backgroundColor: clr, opacity: 0.8, zIndex: 999 }}>
            <Heading size="headingxl" className="!font-heebo text-[14px] font-bold !text-secondary-primary_white ml-2">
              {String(offerText).includes("-")
                ? `טווח הצעות: ₪${formatOfferText(offerText).replace(" - ", " - ₪")}`
                : `מחיר ההצעה: ₪${formatOfferText(offerText)}`}
            </Heading>
          </div>
        )}

        {/* ✅ עכשיו תמיד מגיע image_url תקין, ולא File */}
        <ImagesCarousel2 images={imagesSafe} />
      </div>

      <div className="flex flex-col gap-7 self-stretch rounded-bl-[16px] rounded-br-[16px] border border-solid border-gray-200 bg-secondary-primary_white p-5">
        <div className="flex flex-col items-center gap-4">
          <div className="flex w-[50%] flex-col items-center gap-0.5">
            <Heading as="h6" className="!font-heebo text-[18px] font-bold !text-black-900">
              {model}
            </Heading>
            <div className="mx-2.5 flex items-center justify-center gap-2.5 self-stretch">
              <Text as="p" className="text-[14px] font-normal">{year}</Text>
              <div className="h-[6px] w-[6px] rounded-[3px] bg-red-a700_03" />
              <Text as="p" className="text-[14px] font-normal">{manufacturer}</Text>
            </div>
          </div>

          <div className="flex self-stretch">
            <div className="flex w-full flex-col items-center gap-1.5 px-1">
              <Img src="/images/img_icon_black_900_01.svg" alt="מנוע" className="h-[18px]" />
              <Text as="p" className="text-[14px] font-normal">{engine_type}</Text>
            </div>

            <div className="flex w-full flex-col items-center gap-1.5 px-9 sm:px-5">
              <Img src="/images/img_artboard_2_copy_3_black_900_03_3.svg" alt="יד" className="h-[18px]" />
              <Text as="p" className="text-[14px] font-normal">{hand}</Text>
            </div>

            <div className="flex w-full flex-col items-center gap-2 px-6 sm:px-5">
              <Img src="/images/img_icon_black_900.svg" alt="גיר" className="h-[18px]" />
              <Text as="p" className="text-[14px] font-normal">{gearbox}</Text>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
